import React, { useState } from "react";
import PropTypes from "prop-types";
import { Accordion, Button, Divider, Icon, Segment } from "semantic-ui-react";
import EnumList from "./enum-list";

const TypeBadge = ({ type }) => {
  const color =
    type === "object"
      ? "grey"
      : type === "array"
        ? "blue"
        : type === "string"
          ? "green"
          : type === "integer" || type === "number"
            ? "orange"
            : type === "boolean"
              ? "teal"
              : "grey";
  return (
    <Button size="tiny" color={color} style={{ marginLeft: "1em" }}>
      {type}
    </Button>
  );
};
TypeBadge.propTypes = { type: PropTypes.string.isRequired };

const FieldInformation = ({ fields }) => {
  if (!fields || fields.length === 0) return null;

  return (
    <div style={{ marginLeft: "1em", fontSize: "0.9em", color: "grey" }}>
      {fields.map(
        ([label, value]) =>
          value && (
            <div key={label} style={{ marginBottom: "0.5em" }}>
              <strong>{label}</strong> - {value}
            </div>
          ),
      )}
    </div>
  );
};
FieldInformation.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.array).isRequired,
};

const JsonSchemaViewer = ({ schema, name, depth }) => {
  const [open, setOpen] = useState(depth < 1);

  if (!schema) return null;

  let typeStr = schema.type || "";
  if (Array.isArray(typeStr)) typeStr = typeStr.join(" | ");
  const enumVals = schema.enum || schema.enumValues;

  const req = schema["x-required"] ? (
    <Button color="red" size="tiny" style={{ marginLeft: "1em" }}>
      Required
    </Button>
  ) : null;

  const infoFields = [
    ["Label", schema["x-label"]],
    ["Description", schema["x-description"]],
    ["Format", schema.format],
  ];

  if (typeStr === "object" && schema.properties) {
    return (
      <>
        <Accordion style={{ alignItems: "center" }} fluid>
          <Accordion.Title
            active={open}
            onClick={() => setOpen((o) => !o)}
            style={{
              cursor: "pointer",
            }}
          >
            <div style={{ alignItems: "center" }}>
              <Icon name={"dot circle"} />
              <span>
                {name && (
                  <>
                    <span style={{ fontWeight: 600 }}>{name}</span>
                    <TypeBadge type={typeStr} />
                    {req}
                  </>
                )}
              </span>
            </div>
          </Accordion.Title>
          <Accordion.Content active={open} style={{ paddingLeft: 8 }}>
            <FieldInformation fields={infoFields} />
            <EnumList values={enumVals} />
            <Divider />
            {Object.entries(schema.properties).map(([k, v]) => (
              <>
                <JsonSchemaViewer
                  key={k}
                  schema={v}
                  name={k}
                  depth={depth + 1}
                />
                <Divider />
              </>
            ))}
          </Accordion.Content>
        </Accordion>
      </>
    );
  }

  if (typeStr === "array" && schema.items) {
    return (
      <>
        <Accordion fluid>
          <Accordion.Title
            active={open}
            onClick={() => setOpen((o) => !o)}
            style={{
              cursor: "pointer",
            }}
          >
            <div style={{ alignItems: "center" }}>
              <Icon name={"dot circle"} />
              <span>
                {name && (
                  <>
                    <span style={{ fontWeight: 600 }}>{name}</span>
                    <TypeBadge type={typeStr} />
                    {req}
                  </>
                )}
              </span>
            </div>
          </Accordion.Title>
          <Accordion.Content
            active={open}
            style={{ background: "#fff", borderRadius: 6, paddingLeft: 8 }}
          >
            <FieldInformation fields={infoFields} />
            <EnumList values={enumVals} />
            <Divider />
            <JsonSchemaViewer
              schema={schema.items}
              name={`${name} - item`}
              depth={depth + 1}
            />
            <Divider />
          </Accordion.Content>
        </Accordion>
      </>
    );
  }

  // Primitive
  return (
    <Segment
      basic
      style={{
        marginLeft: 18 * depth,
        background: "#f4f7fa",
        borderRadius: 8,
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0.5em 1em",
        }}
      >
        <span style={{ fontWeight: 500, fontSize: "1em" }}>
          {name}
          <TypeBadge type={typeStr} />
          {req}
        </span>
        <EnumList values={enumVals} />
      </div>
      <FieldInformation fields={infoFields} />
    </Segment>
  );
};

JsonSchemaViewer.propTypes = {
  schema: PropTypes.object.isRequired,
  name: PropTypes.string,
  depth: PropTypes.number,
};
JsonSchemaViewer.defaultProps = {
  name: undefined,
  depth: 0,
};

export default JsonSchemaViewer;
